

import React, { useState } from 'react';
// This component implements a simple UI for managing concierge tickets and quotes.
// It relies on Material UI for styling and the amazon‑cognito‑identity‑js package for
// authentication.  Before running this component you will need to install its
// dependencies:
//   npm install @mui/material @mui/icons-material @emotion/react @emotion/styled amazon-cognito-identity-js

import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Alert,
} from '@mui/material';

import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

/**
 * A small helper component for rendering tab contents.  Material UI's Tabs API
 * expects you to hide and show panels manually; this helper does that.
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

/**
 * The main application component.  It encapsulates login via Amazon Cognito
 * together with a few simple screens for listing and manipulating tickets
 * and uploading quotes.  You can change the API_BASE_URL, UserPoolId and
 * ClientId values below to match your deployment; everything else should
 * work out of the box.
 */
export default function App() {
  const API_BASE_URL = 'https://api.enscypherai.com/concierge';
  // Amazon Cognito user pool configuration.  Update these to match your Cognito
  // setup.  See https://github.com/aws-amplify/amplify-js/tree/main/packages/amazon-cognito-identity-js
  const userPool = new CognitoUserPool({
    UserPoolId: 'eu-west-2_Uv2zziNlS',
    ClientId: '7l0csmnebraiplca9tgk90clvn',
  });
  // UI state for authentication.  Cognito can be configured to allow logins via
  // email alias; in that case you should enter your email address here.  We
  // capture it in a variable called `email` for clarity.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [loginError, setLoginError] = useState('');

  // When a Cognito user signs in for the first time or if an admin reset
  // their password, Cognito may require a new password to be set.  These
  // variables help us capture and complete that challenge.  If
  // `passwordChallengeUser` is non‑null, the UI will display a form for
  // entering a new password.
  const [passwordChallengeUser, setPasswordChallengeUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Tabs
  const [currentTab, setCurrentTab] = useState(0);

  // State for ticket list
  const [buyerId, setBuyerId] = useState('');
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState('');

  // State for selected ticket details
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [ticketError, setTicketError] = useState('');
  const [assignConciergeId, setAssignConciergeId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState('');

  // State for uploading a quote
  const [quoteForm, setQuoteForm] = useState({
    procurement_request_id: '',
    quote_title: '',
    total_charge: '',
    qty: '',
    charge_per_item: '',
    shipping_charge: '',
    assigned_concierge_id: '',
    seller_signer: '',
    seller_id: '',
    quote_id: '',
    file: null,
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');

  // State for quotes by seller
  const [sellerId, setSellerId] = useState('');
  const [sellerQuotes, setSellerQuotes] = useState([]);
  const [sellerQuotesLoading, setSellerQuotesLoading] = useState(false);
  const [sellerQuotesError, setSellerQuotesError] = useState('');

  // Helper: create Authorization header when we have a token
  const authHeader = () => {
    return idToken ? { Authorization: idToken } : {};
  };

  /**
   * Attempt to authenticate the user via Cognito.  On success we
   * extract the idToken for use in API requests.
   */
  const handleLogin = () => {
    setLoginError('');
    const authenticationDetails = new AuthenticationDetails({
      Username: email, // use email as the Cognito username alias
      Password: password,
    });
    const cognitoUser = new CognitoUser({
      // Cognito accepts either the username or an email alias depending on your pool configuration.
      Username: email,
      Pool: userPool,
    });
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const token = result.getIdToken().getJwtToken();
        setIdToken(token);
        setUser(cognitoUser);
      },
      onFailure: (err) => {
        setLoginError(err.message || JSON.stringify(err));
      },
      // Cognito invokes this callback when the user must set a new password
      // before completing authentication.  We store the user object so we can
      // call completeNewPasswordChallenge from a different handler.
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Remove attributes that Cognito does not allow to be updated here.  If
        // these are included when we call completeNewPasswordChallenge Cognito
        // will reject the request.  See https://github.com/aws/aws-amplify/blob/main/packages/amazon-cognito-identity-js/README.md
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        delete userAttributes.email;
        delete userAttributes.phone_number;
        setPasswordChallengeUser({ cognitoUser, userAttributes, requiredAttributes });
      },
    });
  };

  /**
   * Signs the current user out and clears authentication state.
   */
  const handleLogout = () => {
    if (user) {
      user.signOut();
    }
    setUser(null);
    setIdToken(null);
    setEmail('');
    setPassword('');
    setLoginError('');
    setPasswordChallengeUser(null);
    setNewPassword('');
  };

  /**
   * Query tickets for a given buyer via GET /buyers/{buyer_id}/tickets.
   */
  const fetchTickets = async () => {
    if (!buyerId) return;
    setTickets([]);
    setTicketsLoading(true);
    setTicketsError('');
    try {
      const response = await fetch(`${API_BASE_URL}/buyers/${buyerId}/tickets`, {
        method: "GET",
        headers: {
          ...authHeader(),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch tickets (status ${response.status})`);
      }
      const data = await response.json();
      setTickets(data);
    } catch (error) {
      setTicketsError(error.message);
    } finally {
      setTicketsLoading(false);
    }
  };

  /**
   * Fetch an individual ticket and its associated quotes via GET /tickets/{id}.
   */
  const fetchTicketDetails = async (ticketId) => {
    setSelectedTicketId(ticketId);
    setTicketDetails(null);
    setQuotes([]);
    setTicketError('');
    try {
      const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}`, {
        headers: {
          ...authHeader(),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch ticket (status ${response.status})`);
      }
      const data = await response.json();
      setTicketDetails(data.ticket);
      setQuotes(data.quotes || []);
    } catch (error) {
      setTicketError(error.message);
    }
  };

  /**
   * Assign the selected ticket to a concierge via POST /tickets/assign.
   */
  const handleAssignTicket = async () => {
    if (!assignConciergeId || !selectedTicketId) return;
    setAssignError('');
    setAssigning(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tickets/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify({
          procurement_request_id: selectedTicketId,
          concierge_id: assignConciergeId,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to assign ticket (status ${response.status})`);
      }
      // Refresh ticket details after assignment
      await fetchTicketDetails(selectedTicketId);
      setAssignConciergeId('');
    } catch (error) {
      setAssignError(error.message);
    } finally {
      setAssigning(false);
    }
  };

  /**
   * Close the selected ticket via POST /tickets/close.
   */
  const handleCloseTicket = async () => {
    if (!selectedTicketId) return;
    setCloseError('');
    setCloseLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/tickets/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify({
          procurement_request_id: selectedTicketId,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to close ticket (status ${response.status})`);
      }
      // Refresh ticket details after closing
      await fetchTicketDetails(selectedTicketId);
    } catch (error) {
      setCloseError(error.message);
    } finally {
      setCloseLoading(false);
    }
  };

  /**
   * Submit a quote with an attached file via POST /quotes/{procurement_request_id}.
   */
  const handleUploadQuote = async () => {
    if (!quoteForm.procurement_request_id || !quoteForm.file) {
      setUploadError('Procurement request ID and file are required');
      return;
    }
    setUploadError('');
    setUploadLoading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('quote_title', quoteForm.quote_title);
      formData.append('total_charge', quoteForm.total_charge);
      formData.append('qty', quoteForm.qty);
      formData.append('charge_per_item', quoteForm.charge_per_item);
      formData.append('shipping_charge', quoteForm.shipping_charge);
      formData.append('assigned_concierge_id', quoteForm.assigned_concierge_id);
      formData.append('seller_signer', quoteForm.seller_signer);
      if (quoteForm.seller_id) formData.append('seller_id', quoteForm.seller_id);
      if (quoteForm.quote_id) formData.append('quote_id', quoteForm.quote_id);
      formData.append('file', quoteForm.file);
      const response = await fetch(`${API_BASE_URL}/quotes/${quoteForm.procurement_request_id}`, {
        method: 'POST',
        headers: {
          ...authHeader(),
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Failed to upload quote (status ${response.status})`);
      }
      const data = await response.json();
      setUploadResult(data);
      // If we are currently viewing this ticket, refresh its details
      if (selectedTicketId === quoteForm.procurement_request_id) {
        await fetchTicketDetails(selectedTicketId);
      }
      // Reset form
      setQuoteForm({
        procurement_request_id: '',
        quote_title: '',
        total_charge: '',
        qty: '',
        charge_per_item: '',
        shipping_charge: '',
        assigned_concierge_id: '',
        seller_signer: '',
        seller_id: '',
        quote_id: '',
        file: null,
      });
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploadLoading(false);
    }
  };

  /**
   * Get quotes belonging to a specific seller via GET /sellers/{seller_id}/quotes.
   */
  const fetchQuotesBySeller = async () => {
    if (!sellerId) return;
    setSellerQuotes([]);
    setSellerQuotesLoading(true);
    setSellerQuotesError('');
    try {
      const response = await fetch(`${API_BASE_URL}/sellers/${sellerId}/quotes`, {
        headers: {
          ...authHeader(),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch quotes (status ${response.status})`);
      }
      const data = await response.json();
      setSellerQuotes(data);
    } catch (error) {
      setSellerQuotesError(error.message);
    } finally {
      setSellerQuotesLoading(false);
    }
  };

  /**
   * Complete the Cognito new password challenge.  When Cognito returns
   * `newPasswordRequired` during authenticateUser, we prompt the user for a
   * replacement password.  Calling this function will submit the new
   * password and, on success, log the user in.
   */
  const handleCompleteNewPassword = () => {
    if (!passwordChallengeUser || !newPassword) return;
    const { cognitoUser, userAttributes, requiredAttributes } = passwordChallengeUser;
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (result) => {
        const token = result.getIdToken().getJwtToken();
        setIdToken(token);
        setUser(cognitoUser);
        setPasswordChallengeUser(null);
        setNewPassword('');
      },
      onFailure: (err) => {
        setLoginError(err.message || JSON.stringify(err));
        setPasswordChallengeUser(null);
        setNewPassword('');
      },
    });
  };

  // Render
  return (
    <Box>
      {/* Top bar with title and log out button */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Concierge Ticket Management
          </Typography>
          {user && (
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* If no user is logged in and we are not in the middle of a password reset challenge, show login form */}
      {!user && !passwordChallengeUser && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Login</Typography>
          {loginError && <Alert severity="error" sx={{ mt: 2 }}>{loginError}</Alert>}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <Button variant="contained" onClick={handleLogin}>Login</Button>
          </Box>
        </Box>
      )}

      {/* When Cognito requires the user to set a new password, we show this form */}
      {!user && passwordChallengeUser && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6">Set New Password</Typography>
          {loginError && <Alert severity="error" sx={{ mt: 2 }}>{loginError}</Alert>}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />
            <Button variant="contained" onClick={handleCompleteNewPassword}>
              Set Password
            </Button>
          </Box>
        </Box>
      )}

      {/* Authenticated area with tabs */}
      {user && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} centered>
            <Tab label="Tickets" />
            <Tab label="Quote Upload" />
            <Tab label="Quotes by Seller" />
          </Tabs>

          {/* Tickets Tab */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6">Find Tickets by Buyer</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                <TextField
                  label="Buyer ID"
                  value={buyerId}
                  onChange={(e) => setBuyerId(e.target.value)}
                  size="small"
                />
                <Button variant="contained" onClick={fetchTickets}>Search</Button>
              </Box>
              {ticketsLoading && (
                <Box sx={{ mt: 2 }}><CircularProgress /></Box>
              )}
              {ticketsError && (
                <Alert severity="error" sx={{ mt: 2 }}>{ticketsError}</Alert>
              )}
              {!ticketsLoading && !ticketsError && tickets.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Request ID</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tickets.map((ticket) => (
                        <TableRow key={ticket.procurement_request_id}>
                          <TableCell>{ticket.procurement_request_id}</TableCell>
                          <TableCell>{ticket.product_name}</TableCell>
                          <TableCell>{ticket.qty}</TableCell>
                          <TableCell>{ticket.status}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => fetchTicketDetails(ticket.procurement_request_id)}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Ticket detail section */}
              {selectedTicketId && ticketDetails && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6">Ticket Details</Typography>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1">Request ID: {ticketDetails.procurement_request_id}</Typography>
                    <Typography>Buyer ID: {ticketDetails.buyer_id}</Typography>
                    <Typography>Product: {ticketDetails.product_name}</Typography>
                    <Typography>SKU: {ticketDetails.sku}</Typography>
                    <Typography>Qty: {ticketDetails.qty}</Typography>
                    <Typography>Packaging: {ticketDetails.packaging}</Typography>
                    <Typography>Status: {ticketDetails.status}</Typography>
                    <Typography>Assigned Concierge: {ticketDetails.assigned_concierge_id || 'Unassigned'}</Typography>
                    <Typography>Conversation Summary: {ticketDetails.conversation_summary}</Typography>
                  </Paper>
                  {/* Assign concierge */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Assign Concierge</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                      <TextField
                        label="Concierge ID"
                        value={assignConciergeId}
                        onChange={(e) => setAssignConciergeId(e.target.value)}
                        size="small"
                      />
                      <Button
                        variant="contained"
                        onClick={handleAssignTicket}
                        disabled={assigning}
                      >
                        Assign
                      </Button>
                    </Box>
                    {assignError && <Alert severity="error" sx={{ mt: 1 }}>{assignError}</Alert>}
                  </Box>
                  {/* Close ticket */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Close Ticket</Typography>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleCloseTicket}
                      disabled={closeLoading}
                    >
                      Close Ticket
                    </Button>
                    {closeError && <Alert severity="error" sx={{ mt: 1 }}>{closeError}</Alert>}
                  </Box>
                  {/* Quotes associated with the ticket */}
                  <Box>
                    <Typography variant="h6">Quotes</Typography>
                    {quotes.length === 0 ? (
                      <Typography>No quotes uploaded.</Typography>
                    ) : (
                      <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Quote ID</TableCell>
                              <TableCell>Title</TableCell>
                              <TableCell>Total Charge</TableCell>
                              <TableCell>Qty</TableCell>
                              <TableCell>Unit Price</TableCell>
                              <TableCell>Shipping</TableCell>
                              <TableCell>Seller</TableCell>
                              <TableCell>File</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {quotes.map((q) => (
                              <TableRow key={q.quote_id}>
                                <TableCell>{q.quote_id}</TableCell>
                                <TableCell>{q.quote_title}</TableCell>
                                <TableCell>{q.total_charge}</TableCell>
                                <TableCell>{q.qty}</TableCell>
                                <TableCell>{q.charge_per_item}</TableCell>
                                <TableCell>{q.shipping_charge}</TableCell>
                                <TableCell>{q.seller_id}</TableCell>
                                <TableCell>
                                  {q.quote_file_path && (
                                    <a
                                      href={q.quote_url || q.quote_file_path}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      View
                                    </a>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Quote upload tab */}
          <TabPanel value={currentTab} index={1}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6">Upload Quote</Typography>
              {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}
              {uploadResult && <Alert severity="success" sx={{ mt: 2 }}>Quote uploaded successfully!</Alert>}
              <Box
                component="form"
                noValidate
                autoComplete="off"
                sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}
              >
                <TextField
                  label="Procurement Request ID"
                  value={quoteForm.procurement_request_id}
                  onChange={(e) => setQuoteForm({ ...quoteForm, procurement_request_id: e.target.value })}
                  required
                />
                <TextField
                  label="Quote Title"
                  value={quoteForm.quote_title}
                  onChange={(e) => setQuoteForm({ ...quoteForm, quote_title: e.target.value })}
                />
                <TextField
                  label="Total Charge"
                  value={quoteForm.total_charge}
                  onChange={(e) => setQuoteForm({ ...quoteForm, total_charge: e.target.value })}
                />
                <TextField
                  label="Quantity"
                  value={quoteForm.qty}
                  onChange={(e) => setQuoteForm({ ...quoteForm, qty: e.target.value })}
                />
                <TextField
                  label="Charge per Item"
                  value={quoteForm.charge_per_item}
                  onChange={(e) => setQuoteForm({ ...quoteForm, charge_per_item: e.target.value })}
                />
                <TextField
                  label="Shipping Charge"
                  value={quoteForm.shipping_charge}
                  onChange={(e) => setQuoteForm({ ...quoteForm, shipping_charge: e.target.value })}
                />
                <TextField
                  label="Assigned Concierge ID"
                  value={quoteForm.assigned_concierge_id}
                  onChange={(e) => setQuoteForm({ ...quoteForm, assigned_concierge_id: e.target.value })}
                />
                <TextField
                  label="Seller Signer"
                  value={quoteForm.seller_signer}
                  onChange={(e) => setQuoteForm({ ...quoteForm, seller_signer: e.target.value })}
                />
                <TextField
                  label="Seller ID (optional)"
                  value={quoteForm.seller_id}
                  onChange={(e) => setQuoteForm({ ...quoteForm, seller_id: e.target.value })}
                />
                <TextField
                  label="Quote ID (optional)"
                  value={quoteForm.quote_id}
                  onChange={(e) => setQuoteForm({ ...quoteForm, quote_id: e.target.value })}
                />
                <Button variant="contained" component="label">
                  Select File
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setQuoteForm({ ...quoteForm, file: e.target.files[0] })}
                  />
                </Button>
                {quoteForm.file && <Typography variant="body2">Selected: {quoteForm.file.name}</Typography>}
                <Button
                  variant="contained"
                  onClick={handleUploadQuote}
                  disabled={uploadLoading}
                >
                  {uploadLoading ? <CircularProgress size={20} /> : 'Upload Quote'}
                </Button>
              </Box>
            </Box>
          </TabPanel>

          {/* Quotes by seller tab */}
          <TabPanel value={currentTab} index={2}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6">List Quotes by Seller</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center' }}>
                <TextField
                  label="Seller ID"
                  value={sellerId}
                  onChange={(e) => setSellerId(e.target.value)}
                  size="small"
                />
                <Button variant="contained" onClick={fetchQuotesBySeller}>Search</Button>
              </Box>
              {sellerQuotesLoading && (
                <Box sx={{ mt: 2 }}><CircularProgress /></Box>
              )}
              {sellerQuotesError && (
                <Alert severity="error" sx={{ mt: 2 }}>{sellerQuotesError}</Alert>
              )}
              {!sellerQuotesLoading && !sellerQuotesError && sellerQuotes.length > 0 && (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Quote ID</TableCell>
                        <TableCell>Request ID</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Total Charge</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Shipping</TableCell>
                        <TableCell>Seller</TableCell>
                        <TableCell>File</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sellerQuotes.map((q) => (
                        <TableRow key={q.quote_id}>
                          <TableCell>{q.quote_id}</TableCell>
                          <TableCell>{q.procurement_request_id}</TableCell>
                          <TableCell>{q.quote_title}</TableCell>
                          <TableCell>{q.total_charge}</TableCell>
                          <TableCell>{q.qty}</TableCell>
                          <TableCell>{q.charge_per_item}</TableCell>
                          <TableCell>{q.shipping_charge}</TableCell>
                          <TableCell>{q.seller_id}</TableCell>
                          <TableCell>
                            {q.quote_file_path && (
                              <a
                                href={q.quote_url || q.quote_file_path}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>
        </Box>
      )}
    </Box>
  );
}